'use client'

import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import type * as React from 'react'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { XCloseIcon } from '@/components/ui/icons'
import { cn } from '@/helpers/utils'

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
	return <DialogPrimitive.Root data-slot='dialog' {...props} />
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
	return <DialogPrimitive.Trigger data-slot='dialog-trigger' {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
	return <DialogPrimitive.Portal data-slot='dialog-portal' {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
	return <DialogPrimitive.Close data-slot='dialog-close' {...props} />
}

function DialogOverlay({
	className,
	type,
	...props
}: DialogPrimitive.Backdrop.Props & { type: 'light' | 'dark' }) {
	return (
		<DialogPrimitive.Backdrop
			className={cn(
				'data-open:fade-in-0 data-closed:fade-out-0 fixed inset-0 isolate z-(--z-modal-overlay) bg-black/10 duration-100 data-closed:animate-out data-open:animate-in supports-backdrop-filter:backdrop-blur-xs',
				type === 'light'
					? 'bg-neutral-white/80'
					: 'bg-neutral-black/40',
				className
			)}
			data-slot='dialog-overlay'
			{...props}
		/>
	)
}

function DialogCloseButton({
	buttonClassName,
	...props
}: DialogPrimitive.Close.Props & { buttonClassName?: string }) {
	return (
		<DialogClose
			{...props}
			render={
				<ButtonLegacy
					className={buttonClassName}
					shape='square'
					size='md'
					type='button'
					variant='tertiary'
				/>
			}
		>
			<XCloseIcon size={16} />
			<span className='sr-only'>Close</span>
		</DialogClose>
	)
}

function DialogContent({
	className,
	children,
	showCloseButton = true,
	overlayType = 'dark',
	...props
}: DialogPrimitive.Popup.Props & {
	showCloseButton?: boolean
	overlayType?: 'light' | 'dark'
}) {
	return (
		<DialogPortal>
			<DialogOverlay type={overlayType} />
			<DialogPrimitive.Popup
				className={cn(
					'data-open:fade-in-0 data-open:zoom-in-95 data-closed:fade-out-0 data-closed:zoom-out-95 fixed top-1/2 left-1/2 z-(--z-modal) grid max-h-[calc(100%-2rem)] w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-xl bg-background text-sm outline-none ring-1 ring-foreground/10 duration-100 data-closed:animate-out data-open:animate-in sm:max-w-sm',
					className
				)}
				data-slot='dialog-content'
				{...props}
			>
				{children}
				{showCloseButton && (
					<DialogCloseButton buttonClassName='absolute top-2 right-2' />
				)}
			</DialogPrimitive.Popup>
		</DialogPortal>
	)
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			className={cn(
				'flex flex-col items-center gap-2 border-b px-6 py-4',
				className
			)}
			data-slot='dialog-header'
			{...props}
		>
			<DialogCloseButton className='absolute top-1 left-1' />
			<h1 className='text-center font-bold text-sm'>{props.children}</h1>
		</div>
	)
}

function DialogFooter({
	className,
	showCloseButton = false,
	children,
	...props
}: React.ComponentProps<'div'> & {
	showCloseButton?: boolean
}) {
	return (
		<div
			className={cn(
				'flex flex-col-reverse gap-2 rounded-b-xl border-t bg-white p-4 px-6 py-4 sm:flex-row sm:justify-end',
				className
			)}
			data-slot='dialog-footer'
			{...props}
		>
			{children}
			{showCloseButton && (
				<DialogPrimitive.Close
					render={<ButtonLegacy type='button' variant='outline' />}
				>
					Close
				</DialogPrimitive.Close>
			)}
		</div>
	)
}

function DialogBody({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			className={cn(
				'flex h-full flex-col gap-6 overflow-auto p-6',
				className
			)}
			data-slot='dialog-body'
			{...props}
		/>
	)
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
	return (
		<DialogPrimitive.Title
			className={cn('font-bold text-xl', className)}
			data-slot='dialog-title'
			{...props}
		/>
	)
}

function DialogDescription({
	className,
	...props
}: DialogPrimitive.Description.Props) {
	return (
		<DialogPrimitive.Description
			className={cn(
				'text-base text-neutral-900 *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
				className
			)}
			data-slot='dialog-description'
			{...props}
		/>
	)
}

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
	DialogCloseButton,
	DialogBody
}
